import { IconDefinition, faPenLine, faPlay } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Tooltip, TooltipProps } from '@growtherapy/sprout-ui';

import { needsReLoginAtom } from '../../auth/state';
import { ControlPanelButton } from '../../components';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { RecordingControl as BaseRecordingControl } from '../../session/controls/RecordingControl';
import { RecordingControlMultipleParticipantsToast } from '../../session/controls/RecordingControlMultipleParticipantsToast';
import {
  PATIENT_OPTED_OUT_OF_SESSION_SUMMARIZATION,
  TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
  TRANSCRIPTION_NOT_AVAILABLE_FOR_MULTIPLE_PARTICIPANTS,
  TRANSCRIPTION_PAUSED_LOGIN_REQUIRED,
} from '../../session/controls/constants';
import { useShouldDisableTranscriptionForMultipleParticipants } from '../../session/controls/useShouldDisableTranscriptionForMultipleParticipants';
import { patientConsentAtom } from '../../session/messages/state';
import { toast } from '../../toasts';
import { UserType } from '../../types';
import { AudioRecordingState, ConsentStatus, ConsentUpdateSource, participantCountAtom } from '../../video-sdk';
import { useSendProviderParticipantStateCallback } from '../messages/useSendProviderParticipantStateCallback';
import { inSessionPatientInformationAtom, isSelectedClientUserMinorAtom, providerShortIdAtom } from '../state';
import { ConsentModal } from './ConsentModal';
import { MinorTranscriptionAcknowledgement } from './MinorTranscriptionAcknowledgement';
import { useCombineAudioTrackMediaStreams } from './useCombineAudioTracks';
import { useGetProviderPatientConsent } from './useGetProviderPatientConsent';
import { useRecordProviderTranscriptConsentMutation } from './useRecordProviderTranscriptionConsentMutation';
import { useRecordingControl } from './useRecordingControl';
import { useRequestingClientConsentToast } from './useRequestingClientConsentToast';
import { useStartRecordingCallback } from './useStartRecordingCallback';
import { useStopRecordingCallback } from './useStopRecordingCallback';

/**
 * TODO: Extract contents to parent once feature is launched
 */
function CombineAudioTrackMediaStreams() {
  useCombineAudioTrackMediaStreams();

  return null;
}

export function RecordingControl() {
  const { userClicked } = useTrackEvent();
  const hasMultipleParticipants = useAtomValue(participantCountAtom) > 1;

  const getAppointmentShortId = useAtomCallback(
    (get) => get(inSessionPatientInformationAtom)?.inSessionAppointmentShortId,
  );

  useRequestingClientConsentToast();
  const { patientShortId } = useAtomValue(inSessionPatientInformationAtom) ?? {};
  const providerShortId = useAtomValue(providerShortIdAtom);
  const providerNeedsReLogin = useAtomValue(needsReLoginAtom);
  const setClientTranscriptionConsent = useSetAtom(patientConsentAtom);
  const [hasDisabledTranscriptionForMultipleParticipants, setHasDisabledTranscriptionForMultipleParticipants] =
    useState(false);
  const [isMinorAcknowledgmentStepOpen, setIsMinorAcknowledgmentStepOpen] = useState(false);
  const [hasAcknowledgedClientMinor, setHasAcknowledgedClientMinor] = useState(false);
  const isClientUserMinor = useAtomValue(isSelectedClientUserMinorAtom);
  const shouldShowMinorAcknowledgmentStep = isClientUserMinor && !hasAcknowledgedClientMinor;
  const sendParticipantState = useSendProviderParticipantStateCallback();
  const { recordingState, isVisible } = useRecordingControl();
  const startRecording = useStartRecordingCallback();
  const stopRecording = useStopRecordingCallback();

  const recordProviderConsent = useRecordProviderTranscriptConsentMutation(providerShortId, patientShortId);
  const { providerConsent, patientConsent, patientUpdateSource } = useGetProviderPatientConsent(
    providerShortId,
    patientShortId,
  );

  const requestingClientConsent = patientConsent === ConsentStatus.DECIDING;
  const isRecording = recordingState === AudioRecordingState.RECORDING;

  const [showConsentModal, setShowConsentModal] = useState(false);
  const isTranscriptionOptedOutInClientPortal =
    patientUpdateSource === ConsentUpdateSource.CLIENT_PORTAL && patientConsent === ConsentStatus.OPTED_OUT;

  const toggleRecording = useCallback(
    async (shouldRecord: boolean) => {
      if (shouldRecord) {
        await startRecording();
      } else {
        await stopRecording();
      }
      sendParticipantState();
    },
    [startRecording, stopRecording, sendParticipantState],
  );

  /**
   * The below code is to prevent sessions with multiple participants from surfacing
   * Transcription features due to AI quality issues identified in these types of sessions.
   * It is behind a feature flag and can be removed when we improve AI features for these sessions.
   */

  const shouldShowMultipleParticipantTranscriptionDisabled = useShouldDisableTranscriptionForMultipleParticipants();

  const shouldDisableTranscription =
    isRecording &&
    !hasDisabledTranscriptionForMultipleParticipants &&
    shouldShowMultipleParticipantTranscriptionDisabled;

  useEffect(() => {
    const handleDisableTranscription = async () => {
      await toggleRecording(false);
      toast.enqueue(RecordingControlMultipleParticipantsToast, {
        timeout: TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
      });
      setHasDisabledTranscriptionForMultipleParticipants(true);
    };
    if (shouldDisableTranscription) {
      handleDisableTranscription();
    }
    if (!hasMultipleParticipants) {
      setHasDisabledTranscriptionForMultipleParticipants(false);
    }
  }, [hasMultipleParticipants, isRecording, shouldDisableTranscription, toggleRecording]);

  const requestClientConsent = useCallback(() => {
    setClientTranscriptionConsent({
      value: ConsentStatus.DECIDING,
      lastUpdated: Date.now(),
    });
    sendParticipantState();
  }, [setClientTranscriptionConsent, sendParticipantState]);

  // Adds a minor acknowledgment step to the recording control. If a client is a minor,
  // show this step regardless of whether they have consented to transcription.
  // Upon accepted, start recording if they have consented to transcription, otherwise request consent.
  const handleMinorAcknowledgmentStepCancel = useCallback(() => {
    setIsMinorAcknowledgmentStepOpen(false);
    setHasAcknowledgedClientMinor(true);
  }, []);

  const handleMinorAcknowledgmentStepConfirm = useCallback(() => {
    setIsMinorAcknowledgmentStepOpen(false);
    setHasAcknowledgedClientMinor(true);

    if (patientConsent === ConsentStatus.OPTED_IN) {
      toggleRecording(true);
      userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
        appointmentShortId: getAppointmentShortId()!,
        newRecordingStatus: 'PLAY',
        entityType: UserType.PROVIDER,
      });
    } else {
      requestClientConsent();
    }
  }, [patientConsent, toggleRecording, requestClientConsent, getAppointmentShortId, userClicked]);

  const tooltipProps = useMemo(
    () =>
      isMinorAcknowledgmentStepOpen
        ? ({
            className: '!bg-transparent opacity-100',
            isOpen: true,
            clickable: true,
            text: (
              <MinorTranscriptionAcknowledgement
                onCancel={handleMinorAcknowledgmentStepCancel}
                onConfirm={handleMinorAcknowledgmentStepConfirm}
              />
            ),
          } satisfies Partial<TooltipProps>)
        : undefined,
    [isMinorAcknowledgmentStepOpen, handleMinorAcknowledgmentStepCancel, handleMinorAcknowledgmentStepConfirm],
  );

  const recordOptIn = async () => {
    const { data } = await recordProviderConsent(true);
    const providerIsOptedIn = data?.recordProviderTranscriptionConsent?.isOptedIn;
    if (providerIsOptedIn && shouldShowMinorAcknowledgmentStep) {
      setIsMinorAcknowledgmentStepOpen(true);
    } else if (providerIsOptedIn && patientConsent !== ConsentStatus.OPTED_IN) {
      requestClientConsent();
    } else if (providerIsOptedIn && patientConsent === ConsentStatus.OPTED_IN) {
      toggleRecording(true);
      userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
        appointmentShortId: getAppointmentShortId()!,
        newRecordingStatus: 'PLAY',
        entityType: UserType.PROVIDER,
      });
    }
    setShowConsentModal(false);
    userClicked(EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT, {
      appointmentShortId: getAppointmentShortId()!,
      entityType: UserType.PROVIDER,
    });
  };

  const bothPartiesConsented = patientConsent === ConsentStatus.OPTED_IN && providerConsent === ConsentStatus.OPTED_IN;
  if (!isVisible || providerConsent === ConsentStatus.OPTED_OUT) return null;
  return (
    <>
      {showConsentModal && (
        <ConsentModal onClose={() => setShowConsentModal(false)} recordOptIn={recordOptIn} isOpen={showConsentModal} />
      )}
      <CombineAudioTrackMediaStreams />
      {shouldShowMultipleParticipantTranscriptionDisabled ? (
        <DisabledRecordingControl icon={faPlay} tooltipText={TRANSCRIPTION_NOT_AVAILABLE_FOR_MULTIPLE_PARTICIPANTS} />
      ) : isTranscriptionOptedOutInClientPortal ? (
        <DisabledRecordingControl icon={faPenLine} tooltipText={PATIENT_OPTED_OUT_OF_SESSION_SUMMARIZATION} />
      ) : providerNeedsReLogin ? (
        <DisabledRecordingControl icon={faPlay} tooltipText={TRANSCRIPTION_PAUSED_LOGIN_REQUIRED} />
      ) : (
        <BaseRecordingControl
          className={classNames({
            'bg-lilac-700 hover:bg-lilac-600': bothPartiesConsented,
          })}
          isRecording={isRecording}
          isRequestingConsent={requestingClientConsent}
          consentPending={!bothPartiesConsented}
          onRecordingToggle={(shouldRecord: boolean) => {
            if (shouldShowMinorAcknowledgmentStep && shouldRecord) {
              setIsMinorAcknowledgmentStepOpen(true);
            } else {
              toggleRecording(shouldRecord);
              userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
                appointmentShortId: getAppointmentShortId()!,
                newRecordingStatus: shouldRecord ? 'PLAY' : 'PAUSE',
                entityType: UserType.PROVIDER,
              });
            }
          }}
          tooltipProps={tooltipProps}
          onPendingConsentClick={() => {
            if (providerConsent !== ConsentStatus.OPTED_IN) {
              setShowConsentModal(true);
            } else if (shouldShowMinorAcknowledgmentStep) {
              setIsMinorAcknowledgmentStepOpen(true);
            } else {
              requestClientConsent();
            }
          }}
        />
      )}
    </>
  );
}

function DisabledRecordingControl({ icon, tooltipText }: { icon: IconDefinition; tooltipText: string }) {
  return (
    <Tooltip
      text={tooltipText}
      /**
       * Although this Tooltip wraps a ControlPanelButton, the tooltip only ever
       * shows when the ControlPanelButton is disabled. In order to make this
       * tooltip still accessible for all users, we wrap it in a <div/> so that
       * there is an element to add the tooltip props to and set
       * childIsInteractive={false} (since the ControlPanelButton won't be
       * actually interactive when the tooltip is enabled).
       */
      childIsInteractive={false}
    >
      <div>
        <ControlPanelButton
          disabled={true}
          aria-label={`Transcription is disabled: ${tooltipText}`}
          iconDefinition={icon}
          data-testid='recording-control'
        />
      </div>
    </Tooltip>
  );
}
